import React from 'react';
import { ChevronDown, Download, Eye, RefreshCw } from 'lucide-react';
import { securityService } from '../../services/adminService';
import type { AuditExportFormat } from '../../services/adminService';
import { todayISO } from '../../utils/dateValidation';
import DismissibleError from '../DismissibleError';
import { EmptyTableState, FormModal, RecordsTableCard, StatusBadge } from '../records';
import './AuditLogs.css';

interface AuditActor {
  id: number;
  username: string;
  display_name?: string;
}

interface FormattedAuditDetail {
  label: string;
  value: unknown;
}

interface AuditEvent {
  id: number;
  timestamp: string;
  actor?: AuditActor | null;
  actor_username?: string | null;
  action: string;
  action_display?: string | null;
  module?: string | null;
  affected_entity?: string | null;
  target_record?: string | null;
  entity_id?: string | number | null;
  event_type?: string | null;
  result?: string | null;
  summary?: string | null;
  formatted_details?: FormattedAuditDetail[];
  source_ip?: string | null;
  ip_address?: string | null;
  integrity?: {
    event_hash?: string | null;
    previous_hash?: string | null;
    hash_algorithm?: string | null;
    integrity_status?: string | null;
  } | null;
}

const value = (input: unknown, fallback = '-'): string =>
  input === null || input === undefined || input === '' ? fallback : String(input);

const formatDateTime = (input?: string): string => {
  if (!input) return '-';
  const date = new Date(input);
  return Number.isNaN(date.getTime()) ? input : date.toLocaleString();
};

const friendlyAction = (input?: string): string => {
  const key = value(input, '').toLowerCase();
  const labels: Record<string, string> = {
    user_login_failed: 'Failed Login',
    role_permission_updated: 'Role Permission Updated',
    backup_completed: 'Backup Completed',
    security_event: 'Security Event',
  };
  return labels[key] || value(input).replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const displayAction = (event: AuditEvent): string =>
  value(event.action_display || friendlyAction(event.action));

const eventTypeOptions = [
  ['authentication', 'Authentication'],
  ['user_management', 'User Management'],
  ['department', 'Department'],
  ['role_permission', 'Role / Permission'],
  ['firearm', 'Firearm'],
  ['issuance', 'Issuance'],
  ['return', 'Return'],
  ['incident', 'Incident Report'],
  ['inspection', 'Inspection'],
  ['custody', 'Custody'],
  ['report', 'Report'],
  ['backup_security', 'Backup / Security'],
  ['security_access', 'Security / Access'],
  ['system_governance', 'System Governance'],
  ['system', 'System'],
];

const resultOptions = [
  'Created',
  'Updated',
  'Submitted',
  'Approved',
  'Rejected',
  'Confirmed',
  'Prescribed',
  'Revised',
  'Referred',
  'Resolved',
  'Failed',
  'Locked',
  'Unlocked',
  'Viewed',
  'Downloaded',
  'Deactivated',
  'Success',
  'Recorded',
];
const DATE_RANGE_ERROR = 'Date From cannot be after Date To.';

const emptyAuditFilters = () => ({
  search: '',
  actor: '',
  action: '',
  entity: '',
  event_type: '',
  result: '',
  date: '',
  date_from: '',
  date_to: '',
});

const hasReversedDateRange = (filters: ReturnType<typeof emptyAuditFilters>): boolean =>
  Boolean(filters.date_from && filters.date_to && filters.date_from > filters.date_to);

const resolveExportFilename = (
  contentDisposition: string | undefined,
  format: AuditExportFormat
): string => {
  const fallback = `cybershield-audit-trail.${format}`;
  if (!contentDisposition) return fallback;

  const encodedMatch = contentDisposition.match(/filename\*\s*=\s*([^;]+)/i);
  const filenameMatch = contentDisposition.match(/filename\s*=\s*(?:"([^"]*)"|([^;]+))/i);
  let candidate = (encodedMatch?.[1] || filenameMatch?.[1] || filenameMatch?.[2] || '').trim();
  candidate = candidate.replace(/^"|"$/g, '');

  const encodingParts = candidate.match(/^[^']*'[^']*'(.*)$/);
  if (encodingParts) candidate = encodingParts[1];
  try {
    candidate = decodeURIComponent(candidate);
  } catch {
    return fallback;
  }

  candidate = candidate.split(/[\\/]/).pop() || '';
  candidate = Array.from(candidate)
    .filter((character) => {
      const code = character.charCodeAt(0);
      return code > 0x1f && code !== 0x7f;
    })
    .join('')
    .replace(/[<>:"|?*]/g, '-')
    .replace(/^\.+/, '')
    .trim();

  return candidate && candidate.toLowerCase().endsWith(`.${format}`) ? candidate : fallback;
};

const formatAuditDetailValue = (input: unknown): string => {
  if (input === null || input === undefined || input === '') return '-';
  if (typeof input === 'string' || typeof input === 'number' || typeof input === 'boolean') return String(input);
  if (Array.isArray(input)) return input.map((item) => formatAuditDetailValue(item)).join(', ');
  if (typeof input === 'object') {
    return Object.entries(input as Record<string, unknown>)
      .map(([key, item]) => `${key.replace(/[_-]+/g, ' ')}: ${formatAuditDetailValue(item)}`)
      .join(', ');
  }
  return String(input);
};

const truncateHash = (hash?: string | null): string => {
  if (!hash) return '-';
  return hash.length <= 24 ? hash : `${hash.slice(0, 10)}...${hash.slice(-10)}`;
};

const AuditLogs: React.FC = () => {
  const [logs, setLogs] = React.useState<AuditEvent[]>([]);
  const [filters, setFilters] = React.useState(emptyAuditFilters);
  const [selectedEvent, setSelectedEvent] = React.useState<AuditEvent | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [exportError, setExportError] = React.useState('');
  const [exporting, setExporting] = React.useState<AuditExportFormat | null>(null);
  const [showMoreFilters, setShowMoreFilters] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    if (hasReversedDateRange(filters)) {
      setError(DATE_RANGE_ERROR);
      setLoading(false);
      return;
    }
    try {
      setLogs(await securityService.getAuditLogs(filters));
    } catch {
      setError('Audit trail events are temporarily unavailable.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const exportAuditTrail = async (format: AuditExportFormat) => {
    setExportError('');
    if (hasReversedDateRange(filters)) {
      setError(DATE_RANGE_ERROR);
      return;
    }

    setExporting(format);
    try {
      const result = await securityService.exportAuditLogs(filters, format);
      const objectUrl = URL.createObjectURL(result.blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = resolveExportFilename(result.contentDisposition, format);
      document.body.appendChild(anchor);
      try {
        anchor.click();
      } finally {
        anchor.remove();
        URL.revokeObjectURL(objectUrl);
      }
    } catch {
      setExportError('Audit Trail export could not be generated.');
    } finally {
      setExporting(null);
    }
  };

  React.useEffect(() => { void load(); }, [load]);

  const visible = logs.filter((log) => {
    const searchText = [
      log.actor_username,
      log.actor?.username,
      log.action,
      displayAction(log),
      log.module,
      log.affected_entity,
      log.target_record,
      log.entity_id,
      log.event_type,
      log.result,
      log.summary,
      log.source_ip,
      log.ip_address,
    ].map((part) => value(part, '')).join(' ').toLowerCase();
    const searchMatch = !filters.search || searchText.includes(filters.search.toLowerCase());
    const userMatch = !filters.actor || value(log.actor_username || log.actor?.username).toLowerCase().includes(filters.actor.toLowerCase());
    const actionMatch = !filters.action || [log.action, displayAction(log)]
      .some((candidate) => value(candidate).toLowerCase().includes(filters.action.toLowerCase()));
    const entityMatch = !filters.entity || value(log.module || log.affected_entity).toLowerCase().includes(filters.entity.toLowerCase());
    const eventTypeMatch = !filters.event_type || log.event_type === filters.event_type;
    const resultMatch = !filters.result || value(log.result).toLowerCase() === filters.result.toLowerCase();
    const dateMatch = !filters.date || value(log.timestamp).slice(0, 10) === filters.date;
    return searchMatch && userMatch && actionMatch && entityMatch && eventTypeMatch && resultMatch && dateMatch;
  });

  const clearFilters = () => {
    setFilters(emptyAuditFilters());
    setShowMoreFilters(false);
  };

  return (
    <div className="admin-dashboard">
      <div className="page-heading">
        <div><h1>Audit Trail</h1><p>Filter and review immutable system audit events.</p></div>
      </div>

      {error && (
        <DismissibleError
          message={error}
          onDismiss={() => setError('')}
          onRetry={error === DATE_RANGE_ERROR ? undefined : load}
        />
      )}
      {exportError && <DismissibleError message={exportError} onDismiss={() => setExportError('')} />}

      <RecordsTableCard
        title="Audit Events"
        description="Immutable admin and security activity across the system."
        meta={loading ? 'Loading' : `${visible.length} of ${logs.length}`}
        actions={(
          <>
            <button className="btn btn-secondary" type="button" onClick={load}><RefreshCw size={14} />Refresh</button>
            <button className="btn btn-secondary" type="button" disabled={exporting !== null} onClick={() => void exportAuditTrail('csv')}>
              <Download size={14} />{exporting === 'csv' ? 'Exporting CSV...' : 'Export CSV'}
            </button>
            <button className="btn btn-secondary" type="button" disabled={exporting !== null} onClick={() => void exportAuditTrail('pdf')}>
              <Download size={14} />{exporting === 'pdf' ? 'Exporting PDF...' : 'Export PDF'}
            </button>
          </>
        )}
        toolbar={(
          <div className="audit-filter-toolbar">
            <div className="audit-filter-grid audit-filter-grid--primary">
              <label className="audit-filter-field audit-filter-field--search">
                <span>Search</span>
                <input
                  type="search"
                  aria-label="Search audit events"
                  value={filters.search}
                  onChange={(event) => setFilters({ ...filters, search: event.target.value })}
                  placeholder="Search audit events"
                />
              </label>
              <label className="audit-filter-field">
                <span>Actor</span>
                <input aria-label="Actor filter" value={filters.actor} onChange={(event) => setFilters({ ...filters, actor: event.target.value })} placeholder="Actor / User" />
              </label>
              <label className="audit-filter-field">
                <span>Event Type</span>
                <select aria-label="Event type filter" value={filters.event_type} onChange={(event) => setFilters({ ...filters, event_type: event.target.value })}>
                  <option value="">All event types</option>
                  {eventTypeOptions.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                </select>
              </label>
              <label className="audit-filter-field">
                <span>Result</span>
                <select aria-label="Result filter" value={filters.result} onChange={(event) => setFilters({ ...filters, result: event.target.value })}>
                  <option value="">All results</option>
                  {resultOptions.map((result) => <option key={result} value={result}>{result}</option>)}
                </select>
              </label>
            </div>

            <div className="audit-filter-secondary">
              <div className="audit-date-fields">
                <label className="audit-filter-field">
                  <span>Date From</span>
                  <input aria-label="Date From" type="date" value={filters.date_from} max={filters.date_to || todayISO()} onChange={(event) => setFilters({ ...filters, date_from: event.target.value })} />
                </label>
                <label className="audit-filter-field">
                  <span>Date To</span>
                  <input aria-label="Date To" type="date" value={filters.date_to} min={filters.date_from || undefined} max={todayISO()} onChange={(event) => setFilters({ ...filters, date_to: event.target.value })} />
                </label>
              </div>
              <div className="audit-filter-actions">
                <button
                  className="btn btn-secondary audit-more-filters-button"
                  type="button"
                  aria-expanded={showMoreFilters}
                  aria-controls="audit-more-filters"
                  onClick={() => setShowMoreFilters((current) => !current)}
                >
                  More Filters
                  <ChevronDown className={showMoreFilters ? 'is-expanded' : undefined} size={15} aria-hidden="true" />
                </button>
                <button className="btn btn-secondary" type="button" aria-label="Clear all Audit Trail filters" onClick={clearFilters}>Clear</button>
                {!loading ? <span className="audit-result-count">{visible.length} result{visible.length === 1 ? '' : 's'}</span> : null}
              </div>
            </div>

            {showMoreFilters ? (
              <div className="audit-filter-grid audit-filter-grid--advanced" id="audit-more-filters">
                <label className="audit-filter-field">
                  <span>Action</span>
                  <input aria-label="Action filter" value={filters.action} onChange={(event) => setFilters({ ...filters, action: event.target.value })} placeholder="Action" />
                </label>
                <label className="audit-filter-field">
                  <span>Module / Entity</span>
                  <input aria-label="Module filter" value={filters.entity} onChange={(event) => setFilters({ ...filters, entity: event.target.value })} placeholder="Module / Entity" />
                </label>
              </div>
            ) : null}
          </div>
        )}
      >
        <table className="records-table audit-logs-table">
          <thead><tr><th>Date / Time</th><th>Actor</th><th>Action</th><th>Module / Entity</th><th>Target Record</th><th>Result</th><th>Summary</th><th>View</th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8}><div className="loading-state"><span className="loading-mark" />Loading audit trail...</div></td></tr>
            ) : visible.length === 0 ? (
              <tr><td colSpan={8}><EmptyTableState title="No audit events" description="No audit events matched the current filters." /></td></tr>
            ) : visible.map((log) => (
              <tr key={log.id}>
                <td className="audit-date-cell"><time dateTime={log.timestamp}>{formatDateTime(log.timestamp)}</time></td>
                <td>{value(log.actor_username || log.actor?.username)}</td>
                <td>{displayAction(log)}</td>
                <td>{value(log.module || log.affected_entity)}</td>
                <td>{value(log.target_record || log.entity_id)}</td>
                <td><StatusBadge status={log.result || 'recorded'} label={value(log.result, 'Recorded')} /></td>
                <td><span className="audit-summary-cell">{value(log.summary, 'Audit event recorded.')}</span></td>
                <td><button className="btn btn-secondary btn-sm audit-view-button" type="button" aria-label={`View details for audit event ${log.id}`} onClick={() => setSelectedEvent(log)}><Eye size={14} />View</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </RecordsTableCard>

      {selectedEvent && (
        <FormModal
          open={Boolean(selectedEvent)}
          title="Audit Event Details"
          description={selectedEvent.summary || 'Audit event recorded.'}
          onClose={() => setSelectedEvent(null)}
          closeLabel="Close audit event details"
          size="lg"
        >
          <div className="detail-grid governance-detail-grid">
            {[
              ['Date / Time', formatDateTime(selectedEvent.timestamp)],
              ['Actor', value(selectedEvent.actor_username || selectedEvent.actor?.username)],
              ['Action', displayAction(selectedEvent)],
              ['Module / Entity', value(selectedEvent.module || selectedEvent.affected_entity)],
              ['Target Record', value(selectedEvent.target_record || selectedEvent.entity_id)],
              ['Result', value(selectedEvent.result, 'Recorded')],
              ['IP Address', value(selectedEvent.ip_address || selectedEvent.source_ip)],
            ].map(([label, fieldValue]) => (
              <div key={label}><span>{label}</span><strong>{fieldValue}</strong></div>
            ))}

            {selectedEvent.action_display
              && selectedEvent.action_display !== selectedEvent.action ? (
                <div><span>Raw Action</span><strong>{selectedEvent.action}</strong></div>
              ) : null}

            {selectedEvent.affected_entity
              && selectedEvent.module !== selectedEvent.affected_entity ? (
                <div><span>Raw Entity</span><strong>{selectedEvent.affected_entity}</strong></div>
              ) : null}

            {selectedEvent.formatted_details?.length ? (
              <div className="full-width">
                <span>Formatted Details</span>
                <div className="detail-grid governance-detail-grid">
                  {selectedEvent.formatted_details.map((detail) => (
                    <div key={detail.label}>
                      <span>{detail.label}</span>
                      <strong>{formatAuditDetailValue(detail.value)}</strong>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {selectedEvent.integrity ? (
              <div className="full-width">
                <span>Integrity Verification</span>
                <div className="detail-grid governance-detail-grid">
                  <div><span>Event Hash</span><strong><code>{truncateHash(selectedEvent.integrity.event_hash)}</code></strong></div>
                  <div><span>Previous Hash</span><strong><code>{truncateHash(selectedEvent.integrity.previous_hash)}</code></strong></div>
                  <div><span>Hash Algorithm</span><strong>{value(selectedEvent.integrity.hash_algorithm, 'SHA-256')}</strong></div>
                  <div><span>Integrity Status</span><strong>{value(selectedEvent.integrity.integrity_status, 'Verification Not Run')}</strong></div>
                </div>
              </div>
            ) : null}
          </div>
        </FormModal>
      )}
    </div>
  );
};

export default AuditLogs;
